import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Icon } from '../tokens';
import { Section } from './Terms';
import './Terms.css';
import './Sorteo.css';

// Foto del premio. Colocar el archivo en frontend/public/sorteo/ con este
// nombre; si falta, el slide cae a un placeholder en vez de romperse.
const PRIZE_IMG = '/sorteo/premio.jpg';

// Página de Facebook de Troly. Ya NO es requisito del sorteo: el botón sigue
// en el slide como canal de comunidad, no como condición para participar.
const FB_URL = 'https://www.facebook.com/profile.php?id=61590914843505';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
// Números por página de la lista pública (el backend topa en 200).
const PAGE_SIZE = 100;

/* ── Lista pública de números ────────────────────────────────── */
// Las bases prometen publicar aquí la lista de participantes. Pagina contra
// el servidor (una fila por número, ordenadas del 1 al N) en vez de traerse
// el sorteo completo: la lista crece sin techo mientras dure la campaña.
function ParticipantList() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [cerrado, setCerrado] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPage = useCallback(async (offset) => {
    const { data } = await axios.get(`${API}/api/sorteo/participants`, {
      params: { offset, limit: PAGE_SIZE },
    });
    return data;
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchPage(0)
      .then((data) => {
        if (cancelled) return;
        setRows(data.participants || []);
        setTotal(data.total || 0);
        setCerrado(Boolean(data.cerrado));
        setHasMore(Boolean(data.hasMore));
        setStatus('ok');
      })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, [fetchPage]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await fetchPage(rows.length);
      setRows((prev) => [...prev, ...(data.participants || [])]);
      setTotal(data.total || 0);
      setHasMore(Boolean(data.hasMore));
    } catch { /* el botón sigue disponible para reintentar */ }
    finally { setLoadingMore(false); }
  };

  if (status === 'loading') return <p className="sorteo__list-msg">{t('sorteo.numbers.loading')}</p>;
  if (status === 'error') return <p className="sorteo__list-msg">{t('sorteo.numbers.error')}</p>;

  const closedNotice = cerrado
    ? <p className="sorteo__list-closed">{t('sorteo.numbers.closed')}</p>
    : null;

  if (!rows.length) {
    return <>{closedNotice}<p className="sorteo__list-msg">{t('sorteo.numbers.empty')}</p></>;
  }

  return (
    <>
      {closedNotice}
      <p className="sorteo__list-count">{t('sorteo.numbers.count', { count: total })}</p>
      <ol className="sorteo__list">
        {rows.map((r) => (
          <li key={r.numero} className="sorteo__list-row">
            <span className="sorteo__list-num">#{r.numero}</span>
            {r.perfil
              ? <Link className="sorteo__list-name" to={`/u/${r.perfil}`}>{r.nombre}</Link>
              : <span className="sorteo__list-name">{r.nombre}</span>}
          </li>
        ))}
      </ol>
      {hasMore && (
        <button type="button" className="sorteo__cta sorteo__cta--ghost" onClick={loadMore} disabled={loadingMore}>
          {loadingMore ? t('sorteo.numbers.loading') : t('sorteo.numbers.more')}
        </button>
      )}
    </>
  );
}

export default function Sorteo() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [imgOk, setImgOk] = useState(true);
  const sectionKeys = ['s1', 's2', 's3', 's4', 's5', 's6', 's7', 's8'];

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Sin sesión el CTA lleva al embudo de acceso; con sesión, a los planes.
  const ctaTo = user ? '/precios' : '/trigger';
  // Invitar amigos vive en el perfil (modal del programa de referidos).
  const inviteTo = user ? '/profile' : '/trigger';

  const STEPS = [
    { icon: Icon.userPlus(20), title: t('sorteo.steps.s1_title'), body: t('sorteo.steps.s1_body') },
    { icon: Icon.camera(20),   title: t('sorteo.steps.s2_title'), body: t('sorteo.steps.s2_body') },
    {
      icon: Icon.share(20),
      title: t('sorteo.steps.s3_title'),
      body: (
        <>
          {t('sorteo.steps.s3_body')}{' '}
          <Link to={inviteTo}>{t('sorteo.steps.s3_link')}</Link>
        </>
      ),
    },
  ];

  return (
    <div className="sorteo">
      <Helmet>
        <title>{t('sorteo.meta_title')}</title>
        <meta name="description" content={t('sorteo.meta_desc')} />
        <link rel="canonical" href="https://troly.club/sorteo" />
        <meta property="og:title" content={t('sorteo.meta_title')} />
        <meta property="og:description" content={t('sorteo.meta_desc')} />
        <meta property="og:url" content="https://troly.club/sorteo" />
        <meta property="og:image" content={`https://troly.club${PRIZE_IMG}`} />
      </Helmet>

      <div className="container fade-up">
        <Link to="/" className="terms__back" aria-label={t('sorteo.back_to_home')}>
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 4l-6 6 6 6" />
          </svg>
          {t('sorteo.back_to_home')}
        </Link>

        {/* Slide de apertura: pitch de Troly + premio. */}
        <section className="sorteo__slide">
          <div>
            <p className="sorteo__eyebrow">{t('sorteo.eyebrow')}</p>
            <h1 className="sorteo__title">{t('sorteo.title')}</h1>
            <p className="sorteo__prize-name">{t('sorteo.prize_name')}</p>
            <p className="sorteo__pitch">{t('sorteo.pitch')}</p>
            <div className="sorteo__actions">
              <Link className="sorteo__cta" to={ctaTo}>
                {t('sorteo.cta')} <span aria-hidden="true">→</span>
              </Link>
              <a className="sorteo__cta sorteo__cta--ghost" href={FB_URL} target="_blank" rel="noopener noreferrer">
                {Icon.facebook(16)} {t('sorteo.cta_fb')}
              </a>
            </div>
            <p className="sorteo__deadline">{t('sorteo.deadline')}</p>
          </div>

          <figure className="sorteo__art">
            {imgOk ? (
              // El marco es 9:16; la copia desenfocada de fondo la pinta el CSS
              // con --sorteo-prize-img, así la foto entra completa sin recorte.
              <div className="sorteo__frame" style={{ '--sorteo-prize-img': 'url(' + PRIZE_IMG + ')' }}>
                <img
                  src={PRIZE_IMG}
                  alt={t('sorteo.prize_alt')}
                  width="1080"
                  height="1920"
                  decoding="async"
                  onError={() => setImgOk(false)}
                />
              </div>
            ) : (
              <div className="sorteo__frame sorteo__frame--empty">
                <strong>{t('sorteo.prize_name')}</strong>
                <span>{t('sorteo.prize_alt')}</span>
              </div>
            )}
            <figcaption>{t('sorteo.prize_caption')}</figcaption>
          </figure>
        </section>

        {/* Requisitos: los tres se deben cumplir para recibir número. */}
        <h2 className="sorteo__steps-title">{t('sorteo.steps.title')}</h2>
        <div className="sorteo__steps">
          {STEPS.map((s) => (
            <article key={s.title} className="sorteo__step">
              <div className="sorteo__step-icon">{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </article>
          ))}
        </div>

        <section className="sorteo__numbers">
          <h2>{t('sorteo.numbers.title')}</h2>
          <p>{t('sorteo.numbers.body')}</p>
          <ParticipantList />
        </section>

        <hr className="terms__divider" />

        <h2 className="terms__title">{t('sorteo.legal_title')}</h2>
        <p className="terms__updated">{t('sorteo.updated')}</p>

        {sectionKeys.map((key) => {
          const data = t(`sorteo.${key}`, { returnObjects: true });
          if (!data || typeof data !== 'object') return null;
          return <Section key={key} data={data} />;
        })}

        <div className="sorteo__note">
          <h3>{t('sorteo.regulatory.title')}</h3>
          <p>{t('sorteo.regulatory.body')}</p>
        </div>

        <p className="terms__footer-note">{t('sorteo.footer_note')}</p>
      </div>
    </div>
  );
}
